import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PublicLayout from "../shell/PublicLayout";
import IconButton from "../components/IconButton";
import { AudioIcon, VideoIcon, PlayIcon } from "../components/icons";
import { usePlayer } from "../player/PlayerProvider";
import useRagSearch from "../../hooks/useRagSearch";
import RagAnswerPanel from "../../ReuseableComponents/rag/RagAnswerPanel";
import { sendEvent } from "../../service/ragSearch";
import { trackEvent } from "../../analytics";
import { paths } from "../routing/paths";

/*
 * Redesigned search results (Search Results reference, brief §7.2.2):
 * query summary → navy definition card (RAG resolved_concept: definition,
 * "Start here" link, related-concept chips) → three filter rows (Category /
 * Media / Length, single-select, Clear all) → typed "Start here" cards
 * (glossary terms + series/category collections) → class list rows →
 * designed empty state (never blank; Clear filters + a related concept).
 *
 * Category and Media filter client-side over the returned result set today;
 * they switch to server-side params when the RAG filter work deploys
 * (BACKEND-TODO). Length needs duration data that doesn't exist yet — the
 * row renders disabled with a note.
 *
 * Playing a class row starts the matched moment (items[0].url + start_ms)
 * in the persistent global player.
 */

const LENGTH_CHIPS = ["Under 30 min", "30–60 min", "Over 60 min"];

function mediaKindOf(group) {
  const urls = (group.items || []).map((i) => i.url || "");
  return urls.some((u) => /\.(mp4|m4v|mov|webm)(\?|$)/i.test(u)) ? "video" : "audio";
}

function chipClasses(selected, disabled = false) {
  if (disabled)
    return "flex-shrink-0 font-sans text-[14px] font-semibold leading-5 text-ic-disabledText bg-ic-disabledBg border border-ic-lightGray px-4 py-2.5 rounded-3xl min-h-[44px] cursor-not-allowed";
  return selected
    ? "flex-shrink-0 font-sans text-[14px] font-bold leading-5 text-white bg-ic-blue border border-ic-blue px-4 py-2.5 rounded-3xl cursor-pointer min-h-[44px] focus-visible:outline-none focus-visible:shadow-ic-focus"
    : "flex-shrink-0 font-sans text-[14px] font-semibold leading-5 text-ic-body bg-white border border-ic-borderGray px-4 py-2.5 rounded-3xl cursor-pointer min-h-[44px] transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft";
}

function DefinitionCard({ concept }) {
  return (
    <section className="bg-ic-navy border border-white/10 rounded-xl shadow-ic-raised-dark flex flex-col gap-3" style={{ padding: "clamp(20px, 5vw, 32px)" }}>
      <span className="self-start bg-ic-blue text-white text-[12px] font-bold tracking-[0.5px] px-3 py-1 rounded-xl">
        FROM THE GLOSSARY
      </span>
      <h2 className="m-0 font-bold text-white tracking-[-0.3px] ic-wrap-anywhere" style={{ fontSize: "clamp(24px, 5vw, 28px)", lineHeight: 1.3 }}>
        {concept.name}
      </h2>
      {concept.definition && (
        <p className="m-0 text-[18px] font-normal leading-7 text-ic-offwhite max-w-[640px]">{concept.definition}</p>
      )}
      <Link
        to={paths.concept(concept.slug)}
        className="self-start inline-flex items-center gap-2 min-h-[44px] text-ic-sky text-[16px] font-semibold leading-6 no-underline hover:underline"
      >
        Start here: classes on {concept.name.replace(/\s*\(.*\)$/, "")}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M5 12h14" />
          <path d="M13 6l6 6-6 6" />
        </svg>
      </Link>
      {concept.related?.length > 0 && (
        <div className="flex flex-col gap-2 border-t border-white/10 pt-4">
          <span className="text-[12px] font-bold tracking-[0.5px] uppercase text-ic-lightGray">Related concepts</span>
          <div className="ic-rail flex gap-2 overflow-x-auto pb-1">
            {concept.related.map((r) => (
              <Link
                key={r.slug}
                to={paths.concept(r.slug)}
                className="flex-shrink-0 text-[16px] font-semibold leading-6 text-ic-offwhite bg-white/[0.08] border border-white/25 px-5 py-2.5 rounded-3xl no-underline transition-all duration-150 ease-out hover:border-ic-sky hover:text-ic-sky hover:no-underline focus-visible:outline-none focus-visible:border-ic-sky focus-visible:shadow-ic-focus-dark"
              >
                {r.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function StartHereCard({ kicker, kickerVariant, title, meta, href, lockup }) {
  return (
    <Link
      to={href}
      className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised p-5 no-underline hover:no-underline flex items-center gap-4 transition-all duration-150 ease-out hover:shadow-ic-elevated hover:border-ic-borderGray focus-visible:outline-none focus-visible:shadow-ic-focus-soft"
    >
      <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-ic-navy hidden xs:flex flex-col items-center justify-center">
        <span dir="rtl" className="text-[20px] font-extrabold text-ic-sky leading-6">{lockup}</span>
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <span
          className={`self-start text-[11px] font-bold tracking-[0.5px] px-2.5 py-0.5 rounded-xl ${
            kickerVariant === "primary" ? "bg-ic-blue text-white" : "bg-ic-lightGray text-ic-body"
          }`}
        >
          {kicker}
        </span>
        <span className="text-[18px] font-semibold leading-7 text-black ic-wrap-anywhere">{title}</span>
        {meta && <span className="text-[14px] leading-5 text-ic-slate">{meta}</span>}
      </div>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#186CCE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0">
        <path d="M9 6l6 6-6 6" />
      </svg>
    </Link>
  );
}

function ClassRow({ group, last, requestId, rank }) {
  const player = usePlayer();
  const kind = mediaKindOf(group);
  const firstItem = (group.items || [])[0];

  const play = () => {
    if (!firstItem?.url) return;
    player.load(
      {
        classId: group.class_id,
        title: group.title,
        seriesTitle: group.section || null,
        mediaKind: kind,
        src: firstItem.url,
        streamUrl: null,
        href: group.permalink ? paths.permalink(group.permalink) : null,
        durationHint: 0,
      },
      { autoplay: true, startAt: firstItem.start_ms ? firstItem.start_ms / 1000 : undefined }
    );
    if (requestId) sendEvent({ request_id: requestId, kind: "click", class_id: group.class_id, rank });
  };

  return (
    <div className={`flex items-center gap-3 px-4 py-3.5 ${last ? "" : "border-b border-ic-lightGray"} transition-colors duration-150 ease-out hover:bg-ic-offwhite`}>
      <span aria-label={kind === "video" ? "Video" : "Audio"} className="flex-shrink-0 w-11 h-11 border border-ic-borderGray rounded-2xl hidden xs:inline-flex items-center justify-center text-ic-body">
        {kind === "video" ? <VideoIcon size={22} /> : <AudioIcon size={22} />}
      </span>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        {group.section && (
          <span className="self-start bg-ic-lightGray text-ic-body text-[11px] font-semibold tracking-[0.5px] px-2.5 py-0.5 rounded-xl">
            {group.section}
          </span>
        )}
        {group.permalink ? (
          <Link
            to={paths.permalink(group.permalink)}
            onClick={() => requestId && sendEvent({ request_id: requestId, kind: "click", class_id: group.class_id, rank })}
            className="text-[18px] font-semibold leading-7 text-black no-underline hover:text-ic-blue hover:no-underline ic-wrap-anywhere inline-flex items-center py-2 -my-2 min-h-[44px]"
          >
            {group.title}
          </Link>
        ) : (
          <span className="text-[18px] font-semibold leading-7 text-black ic-wrap-anywhere">{group.title}</span>
        )}
        <span className="text-[14px] leading-5 text-ic-slate ic-wrap-anywhere">
          {group.match_reason || group.summary || (kind === "video" ? "Video" : "Audio")}
        </span>
      </div>
      {firstItem?.url && (
        <IconButton variant="circleOutline" size={48} label={`Play: ${group.title}`} onClick={play}>
          <PlayIcon size={20} />
        </IconButton>
      )}
    </div>
  );
}

export default function SearchResultsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get("q") || "";
  const search = useRagSearch();
  const { runQuery } = search;

  const [category, setCategory] = useState(params.get("category") || null);
  const [media, setMedia] = useState(params.get("media") || null);
  const [showAll, setShowAll] = useState(false);
  const [input, setInput] = useState(q);

  useEffect(() => {
    if (q.trim()) {
      runQuery(q);
      setShowAll(false);
      try {
        trackEvent({ category: "search", action: "search", label: q });
      } catch (_) {}
    }
  }, [q, runQuery]);

  const results = search.results;
  const concept = results?.resolved_concept || null;

  const glossaryGroups = useMemo(
    () => (results?.facets || []).filter((f) => f.facet === "glossary").flatMap((f) => f.groups || []),
    [results]
  );
  const classGroups = useMemo(
    () => (results?.facets || []).filter((f) => f.facet !== "glossary").flatMap((f) => f.groups || []),
    [results]
  );
  const collections = results?.collections || [];

  const sections = useMemo(
    () => [...new Set(classGroups.map((g) => g.section).filter(Boolean))],
    [classGroups]
  );

  const filtered = useMemo(
    () =>
      classGroups.filter(
        (g) => (!category || g.section === category) && (!media || mediaKindOf(g) === media)
      ),
    [classGroups, category, media]
  );

  const totalCount = glossaryGroups.length + classGroups.length + collections.length;
  const filtersActive = Boolean(category || media);
  const clearFilters = () => {
    setCategory(null);
    setMedia(null);
  };

  const setFilterParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const visibleRows = showAll ? filtered : filtered.slice(0, 8);
  const relatedFallback = concept?.related?.[0] || null;

  return (
    <PublicLayout>
      <main className="max-w-[960px] w-full mx-auto px-5 pt-6 pb-10 flex flex-col gap-6">
        {/* Query summary */}
        {q ? (
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="m-0 font-bold text-black tracking-[-0.4px] ic-wrap-anywhere" style={{ fontSize: "clamp(24px, 5.5vw, 36px)", lineHeight: 1.25 }}>
              "{q}"
            </h1>
            {!search.loading && results && (
              <span className="text-[16px] leading-6 text-ic-slate">
                {totalCount} result{totalCount === 1 ? "" : "s"} · searched 15,000+ classes
              </span>
            )}
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (input.trim()) navigate(paths.search(input.trim()));
            }}
            className="flex flex-col gap-3"
          >
            <h1 className="m-0 font-bold text-black tracking-[-0.4px]" style={{ fontSize: "clamp(24px, 5.5vw, 36px)", lineHeight: 1.25 }}>
              Search 15,000+ classes
            </h1>
            <div className="flex gap-2 max-w-[560px]">
              <input
                type="search"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder='Try "bitachon"'
                aria-label="Search"
                className="bg-white text-ic-navy placeholder:text-ic-placeholder font-sans text-[16px] leading-6 px-4 h-12 flex-1 border border-ic-lightGray rounded-3xl transition-all duration-150 ease-out focus:outline-none focus:border-ic-blue focus:shadow-ic-focus-soft"
              />
              <button type="submit" className="bg-ic-blue text-ic-offwhite font-sans text-[16px] px-7 h-12 border-0 rounded-3xl cursor-pointer hover:bg-ic-blueHover focus-visible:outline-none focus-visible:shadow-ic-focus">
                Search
              </button>
            </div>
          </form>
        )}

        {search.loading && (
          <div className="flex flex-col gap-4" aria-busy="true" aria-label="Searching">
            <div className="h-40 rounded-xl bg-ic-navy/90 animate-pulse" />
            <div className="h-10 w-2/3 rounded-3xl bg-ic-lightGray animate-pulse" />
            <div className="h-56 rounded-xl bg-ic-lightGray animate-pulse" />
          </div>
        )}

        {search.error === "rate_limited" && (
          <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised p-8 text-center text-[16px] leading-6 text-ic-body">
            The search service is catching its breath — try again in a few seconds.
          </div>
        )}

        {!search.loading && results && (
          <>
            {concept && <DefinitionCard concept={concept} />}

            {search.kind === "answer" && (
              <RagAnswerPanel
                answer={search.answer}
                citations={search.citations}
                noAnswer={search.answerNoAnswer}
                loading={search.answerLoading}
                requestId={search.answerRequestId}
              />
            )}

            {/* Filters */}
            {(classGroups.length > 0 || filtersActive) && (
              <section className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-[12px] font-bold tracking-[0.5px] uppercase text-ic-slate">Filter results</span>
                  {filtersActive && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="bg-transparent border-0 text-ic-blue font-sans text-[14px] font-semibold leading-5 py-3 px-2 -my-3 min-h-[44px] cursor-pointer underline hover:text-ic-blueHover focus-visible:outline-none focus-visible:shadow-ic-focus-soft rounded-md"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {sections.length > 0 && (
                    <div className="ic-rail flex gap-2 overflow-x-auto pb-0.5 items-center">
                      <span className="flex-shrink-0 w-[72px] text-[14px] font-semibold leading-5 text-ic-body">Category</span>
                      {sections.map((s) => (
                        <button
                          key={s}
                          type="button"
                          aria-pressed={category === s}
                          onClick={() => {
                            const v = category === s ? null : s;
                            setCategory(v);
                            setFilterParam("category", v);
                          }}
                          className={chipClasses(category === s)}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="ic-rail flex gap-2 overflow-x-auto pb-0.5 items-center">
                    <span className="flex-shrink-0 w-[72px] text-[14px] font-semibold leading-5 text-ic-body">Media</span>
                    {["audio", "video"].map((m) => (
                      <button
                        key={m}
                        type="button"
                        aria-pressed={media === m}
                        onClick={() => {
                          const v = media === m ? null : m;
                          setMedia(v);
                          setFilterParam("media", v);
                        }}
                        className={chipClasses(media === m)}
                      >
                        {m === "audio" ? "Audio" : "Video"}
                      </button>
                    ))}
                  </div>
                  <div className="ic-rail flex gap-2 overflow-x-auto pb-0.5 items-center">
                    <span className="flex-shrink-0 w-[72px] text-[14px] font-semibold leading-5 text-ic-body">Length</span>
                    {LENGTH_CHIPS.map((l) => (
                      <span key={l} title="Length filtering arrives with class duration data" className={chipClasses(false, true)}>
                        {l}
                      </span>
                    ))}
                    <span className="flex-shrink-0 text-[12px] leading-4 text-ic-slate">coming soon</span>
                  </div>
                </div>
              </section>
            )}

            {/* Start here — typed results */}
            {(glossaryGroups.length > 0 || collections.length > 0) && !filtersActive && (
              <section className="flex flex-col gap-3">
                <h2 className="m-0 font-bold text-black tracking-[-0.3px]" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
                  Start here
                </h2>
                <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
                  {glossaryGroups.slice(0, 2).map((g) => (
                    <StartHereCard
                      key={`g-${g.class_id}`}
                      kicker="GLOSSARY TERM"
                      title={g.title}
                      meta={g.summary ? undefined : "curated start-here path"}
                      href={g.permalink ? paths.permalink(g.permalink) : paths.search(q)}
                      lockup="א־ב"
                    />
                  ))}
                  {collections.slice(0, 4 - Math.min(2, glossaryGroups.length)).map((c) => (
                    <StartHereCard
                      key={`c-${c.kind}-${c.id}`}
                      kicker={c.kind === "series" ? "SERIES" : c.kind === "category" ? "CATEGORY" : "CONCEPT"}
                      kickerVariant={c.kind === "series" ? "primary" : undefined}
                      title={c.title}
                      meta={c.class_count ? `${c.class_count} classes` : undefined}
                      href={
                        c.kind === "series"
                          ? paths.series(c.permalink || c.id)
                          : c.kind === "concept"
                          ? paths.concept(c.permalink || c.id)
                          : paths.permalink(c.permalink || c.id)
                      }
                      lockup={c.kind === "series" ? "סדרה" : "אוצר"}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Class rows */}
            {filtered.length > 0 ? (
              <section className="flex flex-col gap-3">
                <h2 className="m-0 font-bold text-black tracking-[-0.3px]" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
                  Classes
                </h2>
                <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">
                  {visibleRows.map((g, i) => (
                    <ClassRow key={`${g.class_id}-${i}`} group={g} last={i === visibleRows.length - 1} requestId={search.requestId} rank={i + 1} />
                  ))}
                </div>
                {!showAll && filtered.length > visibleRows.length && (
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={() => setShowAll(true)}
                      className="bg-transparent text-ic-body font-sans text-[16px] font-semibold leading-6 px-6 py-2.5 min-h-[48px] border border-ic-borderGray rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
                    >
                      Show {filtered.length - visibleRows.length} more results
                    </button>
                  </div>
                )}
              </section>
            ) : (
              (classGroups.length > 0 || totalCount === 0) && (
                <section className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised flex flex-col items-center gap-4 text-center" style={{ padding: "clamp(24px, 6vw, 52px)" }}>
                  <div className="w-16 h-16 rounded-full bg-ic-lightGray flex items-center justify-center">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#515F7B" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                      <circle cx="11" cy="11" r="7" />
                      <path d="M20 20l-3.5-3.5" />
                    </svg>
                  </div>
                  <h2 className="m-0 font-bold text-black tracking-[-0.3px]" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
                    {filtersActive ? "No classes match those filters" : `Nothing found for "${q}"`}
                  </h2>
                  <p className="m-0 text-[18px] font-normal leading-7 text-ic-body max-w-[480px]">
                    {filtersActive
                      ? `The archive holds ${classGroups.length} class${classGroups.length === 1 ? "" : "es"} for "${q}" — try removing a filter, or start from a related concept.`
                      : "With 15,000+ classes the topic is probably in the archive under a different name — try a Hebrew term or a related concept."}
                  </p>
                  <div className="flex gap-3 flex-wrap justify-center">
                    {filtersActive && (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="bg-ic-blue text-ic-offwhite font-sans text-[16px] leading-6 px-8 h-12 border-0 rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:bg-ic-blueHover hover:shadow-ic-cta-hover focus-visible:outline-none focus-visible:shadow-ic-focus"
                      >
                        Clear all filters
                      </button>
                    )}
                    <Link
                      to={relatedFallback ? paths.concept(relatedFallback.slug) : paths.search("Emunah")}
                      className="inline-flex items-center text-[16px] font-semibold leading-6 text-ic-body bg-white border border-ic-borderGray px-6 h-12 rounded-3xl no-underline box-border transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue hover:no-underline focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
                    >
                      Browse {relatedFallback?.name || "Emunah"} instead
                    </Link>
                  </div>
                </section>
              )
            )}
          </>
        )}
      </main>
    </PublicLayout>
  );
}
